import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { get, post } from '../api/client';

const AuthContext = createContext(null);

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  CORPORATE_ADMIN: 'corporate_admin',
  HOTEL: 'hotel',
  STAFF: 'staff',
  EVENT_VIEWER: 'event_viewer',
  USER: 'user',
};

/** Where each role lands after signing in. */
export const HOME_FOR = {
  [ROLES.SUPER_ADMIN]: '/admin',
  [ROLES.CORPORATE_ADMIN]: '/corporate',
  [ROLES.HOTEL]: '/hotel',
  [ROLES.STAFF]: '/staff',
  [ROLES.EVENT_VIEWER]: '/viewer',
  // Attendees get the same experience as the app, laid out for the web.
  [ROLES.USER]: '/attendee',
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('gl_user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(Boolean(localStorage.getItem('gl_token')));

  /* Re-validate the stored session on load so a revoked account is caught. */
  useEffect(() => {
    if (!localStorage.getItem('gl_token')) return;
    let alive = true;
    get('/auth/me')
      .then((fresh) => {
        if (!alive) return;
        setUser(fresh);
        localStorage.setItem('gl_user', JSON.stringify(fresh));
      })
      .catch(() => {
        localStorage.removeItem('gl_token');
        localStorage.removeItem('gl_user');
        if (alive) setUser(null);
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await post('/auth/login', { email, password });
    localStorage.setItem('gl_token', res.data.token);
    localStorage.setItem('gl_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  }, []);

  /** Mobile sign-in, step one: the server sends a WhatsApp code. */
  const requestOtp = useCallback(async (mobile) => {
    const res = await post('/auth/otp/request', { mobile });
    return res.data;   // { mobile, expiresInMinutes, devOtp? }
  }, []);

  /** Mobile sign-in, step two: exchange the code for a session. */
  const verifyOtp = useCallback(async (mobile, otp) => {
    const res = await post('/auth/otp/verify', { mobile, otp });
    localStorage.setItem('gl_token', res.data.token);
    localStorage.setItem('gl_user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  }, []);

  const logout = useCallback(() => {
    post('/auth/logout', {}).catch(() => {});
    localStorage.removeItem('gl_token');
    localStorage.removeItem('gl_user');
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    requestOtp,
    verifyOtp,
    logout,
    isSuperAdmin: user?.role === ROLES.SUPER_ADMIN,
    isCorporate: user?.role === ROLES.CORPORATE_ADMIN,
    isHotel: user?.role === ROLES.HOTEL,
    isStaff: user?.role === ROLES.STAFF,
    isAttendee: user?.role === ROLES.USER,
    // Read-only company observer — every write is refused by the API too.
    isViewer: user?.role === ROLES.EVENT_VIEWER,
  }), [user, loading, login, requestOtp, verifyOtp, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
